/**
 * Tibbs at the table: on request only, like Single Didge's Readout and Medal
 * Ceremony (docs/TIBBS-VOICE.md 2). Nothing auto-plays.
 *
 * - At every hole end the readout is chosen silently (select.ts), so the game
 *   memory is the same on every device whether or not anyone asks.
 * - "Hear Tibbs" on the hole-end sheet plays the hole's readout; on the
 *   game-over screen, "Presentation of the Golden Amphora" plays the ceremony.
 * - Online, the first request for a hole (or the ceremony) plays for the whole
 *   table, in sync (the room broadcasts a start time); asking again later is a
 *   replay for that player only. Solo, it just plays.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { mixSeed } from '../../engine/rng';
import type { GameState } from '../../engine';
import type { SeatInfo } from '../game/types';
import { KEYS, load, save } from '../lib/storage';
import { unlockAudio } from '../lib/sound';
import { holeFacts } from './facts';
import { LINE_BY_ID } from './library';
import { ensureName, nameClipUrl, nameKnown, prefetchNames, speakableName } from './names';
import { MUSIC_URL, cachedFiles, lineAvailable, loadManifest, packNameUrl, warmExtended } from './pack';
import { NAME_GAP_MS, planCeremony, planReadout } from './plan';
import type { Cue, NameForm, Step, VoiceManifest } from './plan';
import { composeCeremony, emptyMemory, estimateDur, holeKey, normalizeMemory, selectReadout } from './select';
import type { Ceremony, GameMemory, Readout } from './select';
import { normalizeTibbsSettings, voiceGain } from './settings';
import type { TibbsSettings } from './settings';
import { play, stopTibbs } from './voicePlayer';
import type { Playback, PlayState } from './voicePlayer';

/** Seconds of Largo alone before Tibbs opens the ceremony (a phrase boundary in the recording). */
export const CEREMONY_SOLO_S = 7;
const HISTORY_MAX = 150;
const ACK_MS = 3000;

export type TibbsStatus = 'idle' | 'loading' | 'playing' | 'blocked' | 'done';

export interface TibbsCallIn {
  id: number;
  key: string;
  seat: number | null;
  delayMs: number;
  replay: boolean;
}

interface Stored {
  id: string;
  memory: GameMemory;
}

export interface TibbsApi {
  readout: Readout | null;
  ceremony: Ceremony | null;
  status: (key: string) => TibbsStatus;
  /** What is playing (or last played) and the line on screen now. */
  playingKey: string | null;
  cue: Cue | null;
  /** Ceremony: the beat on screen and the seats whose names have been said. */
  beat: number;
  revealed: number[];
  ceremonyOpen: boolean;
  request: (key: string) => void;
  stop: () => void;
  resume: () => void;
  skipCeremony: () => void;
  closeCeremony: () => void;
  settings: TibbsSettings;
  setSettings: (s: TibbsSettings) => void;
}

export function useTibbs(opts: {
  state: GameState;
  seats: SeatInfo[];
  me: number | null;
  mode: 'solo' | 'online';
  gameId: string;
  seed: number;
  guided: boolean;
  muted: boolean;
  requestOnline?: (key: string) => boolean;
  calls?: TibbsCallIn[];
  notify?: (text: string) => void;
}): TibbsApi {
  const { state, seats, me, mode, gameId, seed, guided, muted } = opts;
  const [settings, setSettingsState] = useState<TibbsSettings>(() => normalizeTibbsSettings(load(KEYS.tibbs, null)));
  const [memory, setMemory] = useState<GameMemory>(() => {
    const s = load<Stored | null>(KEYS.tibbsGame, null);
    return s && s.id === gameId ? normalizeMemory(s.memory) : emptyMemory();
  });
  const memRef = useRef(memory);
  memRef.current = memory;
  const [manifest, setManifest] = useState<VoiceManifest | null>(null);
  const [statuses, setStatuses] = useState<Record<string, TibbsStatus>>({});
  const [playingKey, setPlayingKey] = useState<string | null>(null);
  const [cue, setCue] = useState<Cue | null>(null);
  const [beat, setBeat] = useState(-1);
  const [revealed, setRevealed] = useState<number[]>([]);
  const [ceremonyOpen, setCeremonyOpen] = useState(false);
  const playback = useRef<Playback | null>(null);
  const pendingAck = useRef<ReturnType<typeof setTimeout> | null>(null);
  const settingsRef = useRef(settings);
  settingsRef.current = settings;

  // A new game on this device: fresh memory.
  useEffect(() => {
    const s = load<Stored | null>(KEYS.tibbsGame, null);
    const next = s && s.id === gameId ? normalizeMemory(s.memory) : emptyMemory();
    setMemory(next);
    setStatuses({});
    setCeremonyOpen(false);
  }, [gameId]);

  useEffect(() => {
    if (guided) return;
    void loadManifest().then(setManifest);
  }, [guided]);

  const names = useCallback((seat: number) => seats[seat]?.name || state.players[seat]?.name || `Player ${seat + 1}`, [seats, state.players]);
  const nameKey = seats.map((s, i) => `${s.isBot}:${names(i)}`).join('|');

  // Human names in Tibbs's voice: fetched (and cached) ahead of time.
  useEffect(() => {
    // Wait for the pack's manifest, so computer players' names (which ship in it) are never fetched.
    if (guided || !manifest) return;
    const humans = seats.map((s, i) => (!s.isBot || !manifest.names[names(i)] ? names(i) : null)).filter((n): n is string => !!n && speakableName(n));
    prefetchNames(humans);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nameKey, manifest, guided]);

  // Choose each hole's readout silently when the hole ends; the ceremony when the game ends.
  const over = state.phase === 'holeOver' || state.phase === 'gameOver';
  const endKey = over ? `${state.hole}:${state.phase}` : null;
  useEffect(() => {
    if (!endKey || guided) return;
    let live = true;
    void (async () => {
      const m = manifest ?? (await loadManifest());
      const files = m && mode === 'solo' ? await cachedFiles(m) : null;
      if (!live) return;
      const ctx = {
        seed,
        solo: mode === 'solo',
        me,
        ...(mode === 'solo' ? { history: load<string[]>(KEYS.tibbsSeen, []) } : {}),
        ...(m && files ? { available: (id: string) => lineAvailable(m, files, id) } : {}),
        ...(m ? { durOf: (id: string) => m.lines[id]?.dur } : {}),
      };
      let mem = memRef.current;
      mem = selectReadout(holeFacts(state), mem, ctx).memory;
      if (state.phase === 'gameOver') mem = composeCeremony(state, mem, ctx).memory;
      if (mem !== memRef.current) {
        memRef.current = mem;
        setMemory(mem);
        save(KEYS.tibbsGame, { id: gameId, memory: mem } satisfies Stored);
      }
    })();
    return () => {
      live = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [endKey, guided, gameId]);

  // The table moved on (next hole, new game): Tibbs stops mid-sentence, fading out.
  const phaseKey = `${gameId}:${state.hole}:${over}`;
  const playingRef = useRef<string | null>(null);
  playingRef.current = playingKey;
  useEffect(() => {
    if (playingRef.current && playingRef.current !== 'final') stopTibbs();
  }, [phaseKey]);
  useEffect(() => () => stopTibbs(), []);

  const readout = over && state.phase === 'holeOver' ? (memory.readouts[holeKey(state.hole)] ?? null) : null;
  const ceremony = state.phase === 'gameOver' ? memory.ceremony : null;

  const setStatus = useCallback((key: string, s: TibbsStatus) => setStatuses((m) => (m[key] === s ? m : { ...m, [key]: s })), []);

  /** Resolve every name a playlist needs (bundled, cached, or fetched now, briefly). */
  const nameResolver = useCallback(
    async (m: VoiceManifest | null, seatsNeeded: number[], form: NameForm) => {
      const human = seatsNeeded.filter((s) => !(m && packNameUrl(m, names(s), form)));
      await Promise.race([Promise.all(human.map((s) => ensureName(names(s), form))), new Promise((r) => setTimeout(r, 2500))]);
      return (seat: number, f: NameForm): string | null => {
        const n = names(seat);
        const bundled = m ? packNameUrl(m, n, f) : null;
        if (bundled) return bundled;
        const url = nameClipUrl(n, f);
        return nameKnown(url) ? url : null;
      };
    },
    [names],
  );

  const startPlayback = useCallback(
    async (key: string, delayMs: number) => {
      const mem = memRef.current;
      const isFinal = key === 'final';
      const r = isFinal ? null : mem.readouts[key];
      const c = isFinal ? mem.ceremony : null;
      if (!r && !c) return;
      if (isFinal) {
        setCeremonyOpen(true);
        setBeat(-1);
        setRevealed([]);
      }
      setPlayingKey(key);
      setStatus(key, 'loading');
      const m = manifest ?? (await loadManifest());
      if (m) warmExtended(m);
      const seatsNeeded = isFinal ? (c?.beats.flatMap((b) => b.seats) ?? []) : [...(r?.lead?.seats ?? []), ...(r?.tail?.seats ?? [])];
      const resolve = await nameResolver(m, seatsNeeded, isFinal ? 'proclaim' : 'plain');
      const steps: Step[] = m ? (isFinal ? planCeremony(m, c!, resolve, names) : planReadout(m, r!, resolve, names)) : captionOnly(r, c, names);
      const gain = voiceGain(settingsRef.current, muted);
      if (mode === 'solo') {
        const ids = steps.flatMap((s) => (s.kind === 'cue' && s.cue.reveal === undefined ? [s.cue.lineId] : []));
        const seen = load<string[]>(KEYS.tibbsSeen, []).filter((x) => !ids.includes(x));
        save(KEYS.tibbsSeen, [...seen, ...ids].slice(-HISTORY_MAX));
      }
      playback.current = play({
        steps,
        gain,
        delayMs,
        music: isFinal ? { url: MUSIC_URL, soloS: CEREMONY_SOLO_S } : null,
        onCue: (q) => {
          setCue(q);
          if (q.beat !== undefined) setBeat(q.beat);
          if (q.reveal !== undefined) setRevealed((xs) => (xs.includes(q.reveal!) ? xs : [...xs, q.reveal!]));
        },
        onState: (s: PlayState) => {
          if (s === 'done' || s === 'stopped') {
            setStatus(key, 'done');
            setCue(null);
            if (isFinal) {
              setBeat(c ? c.beats.length : -1);
              setRevealed(c ? [...new Set(c.beats.flatMap((b) => b.seats))] : []);
            }
          } else setStatus(key, s);
        },
      });
    },
    [manifest, muted, mode, names, nameResolver, setStatus],
  );

  const request = useCallback(
    (key: string) => {
      unlockAudio();
      const st = statuses[key];
      if (st === 'loading' || st === 'playing') return;
      if (key === 'final') setCeremonyOpen(true);
      // Online, and nobody has played this yet on this device: ask the room, so the whole table hears it.
      if (mode === 'online' && opts.requestOnline && st !== 'done') {
        setStatus(key, 'loading');
        const sent = opts.requestOnline(key);
        if (pendingAck.current) clearTimeout(pendingAck.current);
        // No answer (the socket died under the tap): play it here anyway.
        pendingAck.current = setTimeout(() => void startPlayback(key, 0), sent ? ACK_MS : 0);
        return;
      }
      void startPlayback(key, 0);
    },
    [mode, opts, setStatus, startPlayback, statuses],
  );

  // Online: someone at the table asked (maybe us). The room says when to start.
  const lastCall = useRef(0);
  useEffect(() => {
    for (const call of opts.calls ?? []) {
      if (call.id <= lastCall.current) continue;
      lastCall.current = call.id;
      const expected = call.key === 'final' ? state.phase === 'gameOver' : state.phase === 'holeOver' && call.key === holeKey(state.hole);
      if (!expected) continue;
      if (pendingAck.current) {
        clearTimeout(pendingAck.current);
        pendingAck.current = null;
      }
      if (call.seat !== null && call.seat !== me && !call.replay) {
        opts.notify?.(`${names(call.seat)} asked Tibbs for ${call.key === 'final' ? 'the presentation' : 'his readout'}`);
      }
      void startPlayback(call.key, call.delayMs);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opts.calls]);

  const stop = useCallback(() => playback.current?.stop(), []);
  const resume = useCallback(() => playback.current?.resume(), []);
  const skipCeremony = useCallback(() => {
    playback.current?.stop();
    const c = memRef.current.ceremony;
    setBeat(c ? c.beats.length : -1);
    setRevealed(c ? [...new Set(c.beats.flatMap((b) => b.seats))] : []);
    setStatus('final', 'done');
  }, [setStatus]);
  const closeCeremony = useCallback(() => {
    playback.current?.stop();
    setCeremonyOpen(false);
  }, []);
  const setSettings = useCallback(
    (s: TibbsSettings) => {
      setSettingsState(s);
      save(KEYS.tibbs, s);
      playback.current?.setGain(voiceGain(s, muted));
    },
    [muted],
  );
  useEffect(() => {
    playback.current?.setGain(voiceGain(settingsRef.current, muted));
  }, [muted]);

  return useMemo(
    () => ({
      readout,
      ceremony,
      status: (key: string) => statuses[key] ?? 'idle',
      playingKey,
      cue,
      beat,
      revealed,
      ceremonyOpen,
      request,
      stop,
      resume,
      skipCeremony,
      closeCeremony,
      settings,
      setSettings,
    }),
    [readout, ceremony, statuses, playingKey, cue, beat, revealed, ceremonyOpen, request, stop, resume, skipCeremony, closeCeremony, settings, setSettings],
  );
}

/** No voice pack on this device yet (offline before it ever loaded): captions alone, at reading pace. */
function captionOnly(r: Readout | null | undefined, c: Ceremony | null | undefined, names: (seat: number) => string): Step[] {
  const out: Step[] = [];
  const add = (lineId: string, seats: number[], beat?: number, kind?: Cue['kind']) => {
    const line = LINE_BY_ID.get(lineId);
    if (!line) return;
    const caption = line.text
      .replace(/\{name2\}/g, seats[1] !== undefined ? names(seats[1]) : '')
      .replace(/\{name\}/g, seats.map(names).join(' and '))
      .replace(/\[[^\]]*\]/g, ' ')
      .replace(/\s+/g, ' ')
      .replace(/\s+([.,])/g, '$1')
      .trim();
    out.push({ kind: 'cue', cue: { lineId, caption, seats, pose: line.pose, ...(beat !== undefined ? { beat } : {}), ...(kind ? { kind } : {}) } });
    for (const s of seats) out.push({ kind: 'cue', cue: { lineId, caption, seats, pose: line.pose, reveal: s, ...(beat !== undefined ? { beat } : {}), ...(kind ? { kind } : {}) } });
    out.push({ kind: 'gap', ms: Math.round(estimateDur(line) * 1000) + NAME_GAP_MS });
  };
  if (r?.lead) add(r.lead.lineId, r.lead.seats);
  if (r?.tail) add(r.tail.lineId, r.tail.seats);
  c?.beats.forEach((b, i) => add(b.lineId, b.seats, i, b.kind));
  return out;
}

/** Per-game seed for vs-computer games (the save's own seed, mixed so it is not the deal). */
export function soloTibbsSeed(gameSeed: number): number {
  return mixSeed(gameSeed, 0x7155);
}
